import React, { useMemo, useState } from 'react';

import { DndContext, type DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Button, Link, Paper, Stack, Typography } from '@mui/material';

import {
  type PackageInclusionMutationPayload,
  getSavedInclusionId,
  getSavedInclusionMode,
} from '~/hooks/apis/inclusions/packageInclusionMutationUtils';
import type { PackageInclusionsByPackageId } from '~/hooks/apis/inclusions/useGetPackageInclusionsByOffer';
import { useUpdatePackageInclusions } from '~/hooks/apis/inclusions/useUpdatePackageInclusions';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import { mergePackageInclusions } from '../OfferPackages/OfferPackages';
import { buildPackageSavePayload } from '../OfferPackages/utils';
import { getString } from '../utils';

import AddInclusionDialog from './AddInclusionDialog';
import type { InclusionAssetEditValue } from './InclusionAssetEditDialog';
import InclusionRow from './InclusionRow';
import {
  type MasterInclusion,
  type MasterInclusionEdit,
  type PackageOption,
  TABLE_GRID_COLUMNS,
  buildPackageInclusionsFromMaster,
  getPackageInclusionIdsToUnassign,
  getPackageOptions,
} from './utils';

interface Props {
  inclusions: Array<MasterInclusion>;
  isLoadingInclusions?: boolean;
  offerId?: string;
  onChange: React.Dispatch<React.SetStateAction<Array<MasterInclusion>>>;
  onSaved?: (inclusions: Array<MasterInclusion>) => void;
  packageInclusions?: PackageInclusionsByPackageId;
  packages?: Array<App.Package>;
  vendorId?: string;
}

function buildAssignedPackageInclusions(
  inclusion: MasterInclusion,
  inclusionId: string | undefined,
  nextValues: MasterInclusionEdit,
  packageOptions: Array<PackageOption>,
  packageInclusions: PackageInclusionsByPackageId | undefined,
  offerId: string,
  fallbackSortOrder: number,
  vendorId: string,
) {
  const assignedPackageIds = new Set(inclusion.assignedPackageIds);
  const assignedPackageInclusionsByPackageId = new Map(
    getAssignedPackageInclusionRows(inclusion, packageInclusions).map((packageInclusion) => [
      packageInclusion.packageId,
      packageInclusion,
    ]),
  );

  return packageOptions
    .filter((option) => assignedPackageIds.has(option.id))
    .map((option) => ({
      category_id: nextValues.categoryId ?? null,
      currency: nextValues.currency,
      ...(inclusionId ? { inclusion_id: inclusionId } : {}),
      is_hidden: nextValues.isHidden,
      name: nextValues.inclusion,
      offer_id: offerId,
      opportunity_name:
        assignedPackageInclusionsByPackageId.get(option.id)?.opportunityName ??
        getPackageOpportunityName(packageInclusions, option.id),
      package_id: option.id,
      package_name: option.name,
      rrp: nextValues.rrp,
      sort_order: assignedPackageInclusionsByPackageId.get(option.id)?.sortOrder ?? fallbackSortOrder,
      style: inclusion.highlighted ? 'highlighted' : null,
      lux_plus_tier: inclusion.luxPlusOnly ? 'base' : null,
      type: nextValues.type,
      unit: nextValues.unit,
      vendor_salesforce_id: vendorId,
    }));
}

function getPackageOpportunityName(packageInclusions: PackageInclusionsByPackageId | undefined, packageId: string) {
  const opportunityName = packageInclusions?.[packageId]?.find(
    (inclusion) => inclusion.opportunity_name,
  )?.opportunity_name;

  return getString(opportunityName);
}

function buildCustomMasterInclusion(
  inclusion: InclusionAssetEditValue,
  assignedPackageIds: Array<string>,
): MasterInclusion {
  return {
    id: `custom-inclusion-${Date.now()}`,
    assignedPackageIds,
    category: inclusion.category,
    categoryId: inclusion.categoryId,
    currency: inclusion.currency,
    highlighted: false,
    inclusion: inclusion.name,
    isCustom: true,
    isHidden: inclusion.isHidden,
    luxPlusOnly: false,
    rrp: inclusion.rrp,
    type: inclusion.type,
    unit: inclusion.unit,
  };
}

function getAssignedPackageInclusionRows(
  inclusion: MasterInclusion,
  existingPackageInclusions: PackageInclusionsByPackageId | undefined,
) {
  if (!inclusion.persistId || !existingPackageInclusions) {
    return [];
  }

  const assignedPackageIds = new Set(inclusion.assignedPackageIds);

  return Object.entries(existingPackageInclusions).flatMap(([packageId, packageInclusions]) => {
    return packageInclusions.flatMap((packageInclusion) => {
      const rowId = getString(packageInclusion.id);
      const inclusionId = getString(packageInclusion.inclusion_id ?? packageInclusion.inclusion?.id);
      const sortOrder = Number(packageInclusion.sort_order);

      if (!rowId || inclusionId !== inclusion.persistId || !assignedPackageIds.has(packageId)) {
        return [];
      }

      return [
        {
          id: rowId,
          packageId,
          opportunityName: getString(packageInclusion.opportunity_name),
          sortOrder: Number.isFinite(sortOrder) ? sortOrder : undefined,
        },
      ];
    });
  });
}

export default function Inclusions({
  inclusions,
  isLoadingInclusions,
  offerId,
  onChange,
  onSaved,
  packageInclusions,
  packages,
  vendorId,
}: Props) {
  const updatePackageInclusions = useUpdatePackageInclusions();
  const { enqueueSnackbar } = useSnackbar();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const packageOptions = useMemo(() => getPackageOptions(packages), [packages]);
  const inclusionIds = useMemo(() => inclusions.map((inclusion) => inclusion.id), [inclusions]);

  const updateInclusion = (id: string, nextValues: Partial<MasterInclusion>) => {
    onChange((currentInclusions) =>
      currentInclusions.map((inclusion) => (inclusion.id === id ? { ...inclusion, ...nextValues } : inclusion)),
    );
  };

  const handleAddCustomInclusion = async (inclusion: InclusionAssetEditValue, assignedPackageIds: Array<string>) => {
    if (!offerId || !vendorId) {
      enqueueSnackbar('Unable to add inclusion for this offer.', { variant: 'warning' });
      throw new Error('Missing offer or vendor ID.');
    }

    const nextInclusion = buildCustomMasterInclusion(inclusion, assignedPackageIds);

    try {
      await updatePackageInclusions.mutateAsync({
        offerId,
        packageInclusions: buildAssignedPackageInclusions(
          nextInclusion,
          undefined,
          nextInclusion,
          packageOptions,
          packageInclusions,
          offerId,
          inclusions.length + 1,
          vendorId,
        ),
      });
      enqueueSnackbar('Custom inclusion added', { variant: 'success' });
    } catch (error) {
      reportError(error);
      enqueueSnackbar(getErrorMessage(error, 'Failed to add inclusion'), { variant: 'error' });
      throw error;
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    onChange((currentInclusions) => {
      const oldIndex = currentInclusions.findIndex((inclusion) => inclusion.id === active.id);
      const newIndex = currentInclusions.findIndex((inclusion) => inclusion.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return currentInclusions;
      }

      return arrayMove(currentInclusions, oldIndex, newIndex);
    });
  };

  const handleSaveInclusionEdit = async (inclusion: MasterInclusion, nextValues: MasterInclusionEdit) => {
    if (!offerId || !vendorId) {
      enqueueSnackbar('Unable to save inclusion for this offer.', { variant: 'warning' });
      throw new Error('Missing offer or vendor ID.');
    }

    if (!inclusion.persistId) {
      updateInclusion(inclusion.id, nextValues);
      return;
    }

    try {
      const inclusionIdToCustomise = inclusion.persistId;

      const response = await updatePackageInclusions.mutateAsync({
        inclusionIdToCustomise,
        offerId,
        packageInclusions: buildAssignedPackageInclusions(
          inclusion,
          inclusionIdToCustomise,
          nextValues,
          packageOptions,
          packageInclusions,
          offerId,
          inclusions.length + 1,
          vendorId,
        ),
      });

      const persistId = getSavedInclusionId(response);

      if (!persistId) {
        throw new Error('Saved inclusion response did not include an inclusion ID.');
      }

      const mode = getSavedInclusionMode(response);
      const nextInclusions = inclusions.map((currentInclusion) =>
        currentInclusion.id === inclusion.id
          ? {
              ...inclusion,
              ...nextValues,
              id: persistId,
              persistId,
              ...(mode === 'customised' ? { isCustom: true } : {}),
            }
          : currentInclusion,
      );

      onChange(nextInclusions);
      enqueueSnackbar(mode === 'customised' ? 'Custom inclusion created' : 'Inclusion saved', { variant: 'success' });
    } catch (error) {
      reportError(error);
      enqueueSnackbar(getErrorMessage(error, 'Failed to save inclusion'), { variant: 'error' });
      throw error;
    }
  };

  const handleSaveChanges = async () => {
    if (!offerId || !vendorId || !packages) {
      enqueueSnackbar('Unable to save changes for this offer.', { variant: 'warning' });
      return;
    }

    const unsavedAssignedInclusions = inclusions.filter(
      (inclusion) => inclusion.assignedPackageIds.length > 0 && !inclusion.persistId,
    );
    const hasInvalidUnsavedInclusions = unsavedAssignedInclusions.some(
      (inclusion) => !inclusion.inclusion.trim() || !inclusion.category.trim() || !inclusion.unit?.trim(),
    );

    if (hasInvalidUnsavedInclusions) {
      enqueueSnackbar('Add a name, category and unit before saving the custom inclusion.', { variant: 'warning' });
      return;
    }

    const packageInclusionsByPackageId = buildPackageInclusionsFromMaster(inclusions, packageOptions);
    const packageInclusionIdsToDelete = getPackageInclusionIdsToUnassign(inclusions, packageInclusions);
    const nextPackages = mergePackageInclusions(packages, packageInclusionsByPackageId, packageInclusions);
    const nextPackageInclusions: Array<PackageInclusionMutationPayload> = nextPackages.flatMap(
      (offerPackage) =>
        buildPackageSavePayload({
          offerId,
          offerPackage,
          vendorId,
        }).packageInclusions,
    );
    const customPackageInclusions = unsavedAssignedInclusions.flatMap((inclusion, index) =>
      buildAssignedPackageInclusions(
        inclusion,
        undefined,
        inclusion,
        packageOptions,
        packageInclusions,
        offerId,
        inclusions.length + index + 1,
        vendorId,
      ),
    );

    try {
      await updatePackageInclusions.mutateAsync({
        offerId,
        packageInclusionIdsToDelete,
        packageInclusions: nextPackageInclusions.concat(customPackageInclusions),
      });
      onSaved?.(inclusions);
      enqueueSnackbar('Changes saved', { variant: 'success' });
    } catch (error) {
      reportError(error);
      enqueueSnackbar(getErrorMessage(error, 'Failed to save changes'), { variant: 'error' });
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">Inclusions</Typography>
          <Typography variant="body1" color="text.secondary">
            Master list of inclusions across all offer packages. Sets the default ordering; each package's own list is
            kept for manual overrides. Manage the source catalogue on the{' '}
            <Link
              href={vendorId ? `/vendors/${vendorId}/inclusions` : undefined}
              target="_blank"
              rel="noreferrer"
              color="inherit"
              underline="always"
              sx={{ pointerEvents: vendorId ? undefined : 'none' }}
            >
              vendor inclusions page
            </Link>
            .
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="text"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            disabled={updatePackageInclusions.isPending}
            onClick={() => setIsAddDialogOpen(true)}
          >
            Create custom inclusion
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={updatePackageInclusions.isPending}
            onClick={() => void handleSaveChanges()}
            sx={{ width: 120 }}
          >
            {updatePackageInclusions.isPending ? 'Saving...' : 'Save changes'}
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Drag <DragIndicatorIcon fontSize="inherit" sx={{ verticalAlign: 'text-bottom' }} /> to reorder. Inclusions
        follow this master order. Any inclusion assigned to only one package is shown first within that package.
      </Typography>

      <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              bgcolor: 'grey.50',
              display: { xs: 'none', lg: 'grid' },
              gap: 2,
              gridTemplateColumns: TABLE_GRID_COLUMNS,
              px: 2,
              py: 1.5,
            }}
          >
            <Box />
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Category
            </Typography>
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Inclusion
            </Typography>
            <Typography variant="overline" align="center" sx={{ fontWeight: 700 }}>
              Highlight
            </Typography>
            <Typography variant="overline" align="center" sx={{ fontWeight: 700 }}>
              LuxPlus only
            </Typography>
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Assigned packages
            </Typography>
            <Box />
          </Box>

          {isLoadingInclusions && (
            <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Loading inclusions...
              </Typography>
            </Box>
          )}

          {!isLoadingInclusions && inclusions.length === 0 && (
            <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No inclusions are currently available for this offer.
              </Typography>
            </Box>
          )}

          {!isLoadingInclusions && inclusions.length > 0 && (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={inclusionIds} strategy={verticalListSortingStrategy}>
                {inclusions.map((inclusion) => (
                  <InclusionRow
                    key={inclusion.id}
                    inclusion={inclusion}
                    packageOptions={packageOptions}
                    onChange={updateInclusion}
                    onSaveEdit={handleSaveInclusionEdit}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </Paper>
      </Box>
      <AddInclusionDialog
        isSaving={updatePackageInclusions.isPending}
        open={isAddDialogOpen}
        packageOptions={packageOptions}
        onAdd={handleAddCustomInclusion}
        onClose={() => setIsAddDialogOpen(false)}
      />
    </Box>
  );
}
